import React, { useState, useEffect } from "react";
import { X, Box, Loader2, Check, Sparkles } from "lucide-react";

const SaveSpriteDialog = ({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  spriteData = null,
  theme = "dark", // "dark" | "light"
}) => {
  const [name, setName] = useState(initialName || "");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const isLight = theme === "light";

  useEffect(() => {
    if (isOpen) {
      setName(initialName || spriteData?.defaultName || "Unnamed Sprite");
      setDescription("");
      setError("");
      setIsSaving(false);
    }
  }, [isOpen, initialName, spriteData]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError("Please enter a name for this sprite.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        spriteCode: spriteData?.spriteCode,
        thumbnail: spriteData?.thumbnail || null,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save sprite:", err);
      setError(err.message || "Failed to save sprite. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden font-sans border-0 ${
          isLight
            ? "bg-white ring-1 ring-neutral-200 text-neutral-800"
            : "bg-[#13151b] ring-1 ring-[#27272a] text-white shadow-[0_25px_60px_rgba(0,0,0,0.9)]"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 py-4 flex items-center justify-between border-0 ${
            isLight
              ? "bg-neutral-50 border-b border-neutral-200"
              : "bg-[#0c0d12] border-b border-[#222226]"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-600/15 ring-1 ring-purple-500/25 flex items-center justify-center text-purple-400">
              <Box className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`font-bold text-base leading-tight ${isLight ? "text-neutral-900" : "text-white"}`}>
                Save Sprite to Backpack
              </h3>
              <p className={`text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                Reuse this character and its scripts in any project
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer border-0 ${
              isLight
                ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-500"
                : "bg-white/5 hover:bg-white/10 text-neutral-300"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Sprite Preview Card */}
          <div
            className={`p-3.5 rounded-xl flex items-center gap-3.5 border-0 ${
              isLight
                ? "bg-purple-50/80 ring-1 ring-purple-200/80 text-purple-900"
                : "bg-purple-950/25 ring-1 ring-purple-500/20 text-purple-200"
            }`}
          >
            <div className="w-14 h-14 rounded-xl bg-white/80 dark:bg-purple-900/30 ring-1 ring-purple-500/30 flex items-center justify-center overflow-hidden flex-shrink-0">
              {spriteData?.thumbnail ? (
                <img
                  src={spriteData.thumbnail}
                  alt={name || "Sprite"}
                  className="w-12 h-12 object-contain"
                />
              ) : (
                <Box className="w-7 h-7 text-purple-400" />
              )}
            </div>

            <div className="flex flex-col overflow-hidden">
              <span className="text-xs font-bold truncate">
                {name || spriteData?.defaultName || "Scratch Sprite"}
              </span>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-600/20 text-purple-700 dark:text-purple-300 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  Costumes & Sounds
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 ring-1 ring-red-200 dark:ring-red-800/40 text-red-600 dark:text-red-300 text-xs font-semibold">
              {error}
            </div>
          )}

          {/* Sprite Name Field */}
          <div className="space-y-1.5 text-left">
            <label className={`text-xs font-bold flex items-center justify-between ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
              <span>Sprite Name *</span>
              <span className="text-[10px] text-neutral-400 font-normal">Max 50 chars</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hero Character"
              maxLength={50}
              autoFocus
              className={`w-full px-3.5 py-2.5 rounded-xl text-sm font-medium outline-none transition border-0 ${
                isLight
                  ? "bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white"
                  : "bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500"
              }`}
            />
          </div>

          {/* Sprite Description Field */}
          <div className="space-y-1.5 text-left">
            <label className={`text-xs font-bold ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Main player sprite with walking and jump animations"
              rows={2}
              maxLength={200}
              className={`w-full px-3.5 py-2 rounded-xl text-sm font-medium outline-none transition resize-none border-0 ${
                isLight
                  ? "bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white"
                  : "bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500"
              }`}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer border-0 ${
                isLight
                  ? "text-neutral-600 hover:bg-neutral-100"
                  : "text-neutral-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="px-5 py-2.5 rounded-xl text-xs font-extrabold bg-purple-600 hover:bg-purple-500 active:scale-95 text-white shadow-md shadow-purple-600/30 flex items-center gap-2 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-0"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Save to Backpack</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveSpriteDialog;
